import DOMPurify from "dompurify";

/**
 * Flag colors used in FocusMap
 */
export const FLAG_COLORS = {
  green: "#8a9a5b",
  yellow: "#d2b773",
  red: "#b76e79",
} as const;

/**
 * Sanitizes HTML content for node labels.
 * Only allows <strong>, <b>, <span>, and <font> tags with color styles.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for storage and display
 */
export function sanitizeLabel(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "strong", "span", "br", "font"],
    ALLOWED_ATTR: ["style", "color"],
  });
}

/**
 * Strips all HTML tags from a label, leaving only plain text.
 * Useful for exports (Excel, PDF) and search functionality.
 * Replaces <br/> tags with spaces to prevent word concatenation.
 *
 * @param html - The HTML string to strip
 * @returns Plain text without HTML markup
 */
export function stripHtmlFromLabel(html: string): string {
  // Replace <br/> and <br> tags with spaces before stripping HTML
  let htmlWithSpaces = html.replace(/<br\s*\/?>/gi, " ");
  // Add space before closing tags to prevent word concatenation
  htmlWithSpaces = htmlWithSpaces.replace(/<\/(b|strong|span|font)>/gi, " ");
  const temp = document.createElement("div");
  temp.innerHTML = htmlWithSpaces;
  // Get text and normalize whitespace (collapse multiple spaces into one)
  const text = temp.textContent || temp.innerText || "";
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Applies text color to the current selection.
 * Uses document.execCommand for contenteditable elements.
 *
 * @param color - Hex color code (e.g., '#8a9a5b')
 */
export function applyTextColor(color: string): void {
  const selection = window.getSelection();
  if (selection && !selection.isCollapsed) {
    document.execCommand("foreColor", false, color);
  }
}

/**
 * Removes color formatting from the current selection.
 * Resets text to default black color.
 */
export function removeTextColor(): void {
  const selection = window.getSelection();
  if (selection && !selection.isCollapsed) {
    document.execCommand("foreColor", false, "#000000");
  }
}

/**
 * Toggles bold formatting for the current selection.
 * Only applies if text is selected.
 */
export function toggleBold(): void {
  const selection = window.getSelection();
  if (selection && !selection.isCollapsed) {
    document.execCommand("bold");
  }
}

/**
 * Saves the current cursor position in a contenteditable element.
 * Returns an object that can be used to restore the position later.
 */
export function saveCursorPosition(
  element: HTMLElement
): { start: number; end: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.startContainer, range.startOffset);
  const start = preCaretRange.toString().length;
  const end = start + range.toString().length;

  return { start, end };
}

/**
 * Restores cursor position in a contenteditable element.
 */
export function restoreCursorPosition(
  element: HTMLElement,
  position: { start: number; end: number } | null
): void {
  if (!position) return;

  const selection = window.getSelection();
  if (!selection) return;

  let charIndex = 0;
  const range = document.createRange();
  range.setStart(element, 0);
  range.collapse(true);

  const nodeStack: Node[] = [element];
  let node: Node | undefined;
  let foundStart = false;
  let foundEnd = false;

  while (!foundEnd && (node = nodeStack.pop())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const nextCharIndex = charIndex + (node.textContent?.length || 0);
      if (
        !foundStart &&
        position.start >= charIndex &&
        position.start <= nextCharIndex
      ) {
        range.setStart(node, position.start - charIndex);
        foundStart = true;
      }
      if (
        foundStart &&
        position.end >= charIndex &&
        position.end <= nextCharIndex
      ) {
        range.setEnd(node, position.end - charIndex);
        foundEnd = true;
      }
      charIndex = nextCharIndex;
    } else {
      let i = node.childNodes.length;
      while (i--) {
        nodeStack.push(node.childNodes[i]);
      }
    }
  }

  if (foundStart) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

/**
 * Clears any active text selection in the document.
 * Useful when clicking on the canvas or switching between nodes.
 */
export function clearTextSelection(): void {
  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }
}

/**
 * Converts inline markdown to HTML for node labels.
 * Supports:
 * - **bold** or __bold__ → <strong>bold</strong>
 * - *italic* or _italic_ → <em>italic</em>
 * - `code` → <code>code</code> (optional)
 *
 * @param text - Text with markdown formatting
 * @returns HTML string safe for node labels
 */
export function convertInlineMarkdownToHtml(text: string): string {
  let html = text;

  // Convert **bold** to <strong> FIRST (before single *)
  html = html.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__([^_]+?)__/g, "<strong>$1</strong>");

  // Convert *italic* to <em> AFTER bold
  html = html.replace(/\*([^*]+?)\*/g, "<em>$1</em>");
  html = html.replace(/_([^_]+?)_/g, "<em>$1</em>");

  // Convert `code` to <code> (optional - uncomment if needed)
  // html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  return html;
}
